import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-background" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto px-4 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8" data-testid="back-home-link">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <h1 className="text-3xl font-bold font-['Outfit'] mb-2">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mb-8">Last updated: February 11, 2026</p>

        <div className="prose prose-sm max-w-none space-y-6 text-foreground/80">
          <section>
            <h2 className="text-lg font-semibold text-foreground">1. Introduction</h2>
            <p>ConnectMail Developments LLC ("ConnectMail," "we," "us," or "our") operates the ConnectMail platform at connectmailagent.com. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our service.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground">2. Information We Collect</h2>
            <p><strong>Account Information:</strong> When you register, we collect your name, email address, phone number, company name, profession, address (city, state, ZIP code), website URL, and social media profile links.</p>
            <p><strong>Company Logo & Images:</strong> If you upload a company logo or additional images for your email signature, we store those files on our servers.</p>
            <p><strong>Payment Information:</strong> Payment processing is handled by Stripe, Inc. We do not store your credit card numbers. Stripe's privacy policy governs your payment data.</p>
            <p><strong>Usage Data:</strong> We automatically collect information about how you interact with our platform, including pages visited, features used, and signature performance metrics.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground">3. Tracking Technologies</h2>
            <p>We use the following third-party tracking technologies:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>LinkedIn Insight Tag:</strong> We use LinkedIn's tracking pixel to measure the effectiveness of our LinkedIn advertising campaigns and to understand how visitors interact with our site after viewing our LinkedIn ads.</li>
              <li><strong>PostHog Analytics:</strong> We use PostHog for product analytics to understand how users interact with our platform and to improve user experience.</li>
            </ul>
            <p>You can opt out of LinkedIn tracking at <a href="https://www.linkedin.com/psettings/guest-controls" target="_blank" rel="noopener noreferrer" className="text-accent underline">LinkedIn Guest Controls</a>.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground">4. How We Use Your Information</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>To create and manage your account and email signatures</li>
              <li>To match you with complementary professionals for the ad marketplace</li>
              <li>To process payments and manage subscriptions</li>
              <li>To generate your digital business card and QR code</li>
              <li>To communicate with you about your account and our services</li>
              <li>To improve our platform and develop new features</li>
              <li>To measure the performance of our advertising campaigns</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground">5. Information Sharing</h2>
            <p>We share your information only in the following circumstances:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Ad Marketplace:</strong> Your name, profession, company, and city/state are visible to other users in the ad marketplace when you have available ad slots.</li>
              <li><strong>Email Signatures:</strong> Information you include in your email signature (name, contact info, company) is shared with email recipients.</li>
              <li><strong>Service Providers:</strong> We share data with Stripe (payments), MongoDB (database hosting), and analytics providers as needed to operate our service.</li>
              <li><strong>Legal Requirements:</strong> We may disclose information if required by law or to protect our rights.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground">6. Data Security</h2>
            <p>We implement industry-standard security measures including encrypted connections (HTTPS/SSL), hashed passwords, and secure token-based authentication to protect your data. However, no method of transmission over the Internet is 100% secure.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground">7. Data Retention</h2>
            <p>We retain your account data for as long as your account is active. If you cancel your subscription, we retain your data for 30 days after the cancellation effective date, after which it may be deleted. You can request deletion of your data at any time by contacting us.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground">8. Your Rights</h2>
            <p>You have the right to access, update, or delete your personal information through your account settings. You may also contact us to request a copy of your data or to exercise any applicable data protection rights.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground">9. Children's Privacy</h2>
            <p>Our service is not directed to individuals under the age of 18. We do not knowingly collect personal information from children.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground">10. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground">11. Contact Us</h2>
            <p>If you have questions about this Privacy Policy, please contact us at:</p>
            <p className="mt-2">
              <strong>ConnectMail Developments LLC</strong><br />
              Troy, Michigan<br />
              <a href="mailto:connectmail@getfreshpass.com" className="text-accent underline">connectmail@getfreshpass.com</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
